import { CommonModule } from '@angular/common';
import { Component } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { TooltipModule } from 'primeng/tooltip';

import { ButtonComponent } from 'src/app/@core/components/ui/button/button.component';
import { DatepickerComponent } from 'src/app/@core/components/ui/datepicker/datepicker.component';
import { SelectButtonComponent } from 'src/app/@core/components/ui/select-button/select-button.component';
import { IUser } from 'src/app/@core/models/model';
import { AuthenticationService } from 'src/app/@core/services/authentication.service';
import { ToastService } from 'src/app/@core/services/toast.service';

import { PeriodFilter, UntappedActionType, UntappedFeature } from '../../models/workspace-overview.model';
import { WorkspaceOverviewStateService } from '../../services/workspace-overview-state.service';

import {
    AskBobStandaloneWidgetComponent,
    BobAssistedHoursWidgetComponent,
    DocumentCountsWidgetComponent,
    DocumentsEnhancedWidgetComponent,
    FeedbackQualityWidgetComponent,
    ProjectsWidgetComponent,
    TimeReclaimedWidgetComponent,
    UntappedPotentialWidgetComponent,
} from './widgets';

/**
 * Workspace Overview Container
 *
 * Owns the toggle bar (User/Admin scope + Week/Month/Quarter/Custom period +
 * custom date range picker + Export). All signal state lives in the injected
 * `WorkspaceOverviewStateService` so every widget in the subtree can read the
 * same values by injecting the service directly — no `@Input()` threading.
 *
 * The service is provided here (component-scoped) so its lifetime matches the
 * dashboard and it is not shared with the rest of the app.
 */
@Component({
    selector: 'app-workspace-overview',
    standalone: true,
    providers: [WorkspaceOverviewStateService],
    imports: [
        CommonModule,
        FormsModule,
        TooltipModule,
        ButtonComponent,
        DatepickerComponent,
        SelectButtonComponent,
        TimeReclaimedWidgetComponent,
        BobAssistedHoursWidgetComponent,
        DocumentsEnhancedWidgetComponent,
        AskBobStandaloneWidgetComponent,
        UntappedPotentialWidgetComponent,
        FeedbackQualityWidgetComponent,
        ProjectsWidgetComponent,
        DocumentCountsWidgetComponent,
    ],
    templateUrl: './workspace-overview.component.html',
    styleUrl: './workspace-overview.component.scss',
})
export class WorkspaceOverviewComponent {
    /** Expose PeriodFilter enum to the template (for the Custom picker guard). */
    readonly PeriodFilter = PeriodFilter;

    /** Two-way date model bound to the range picker; state is written to the service. */
    customDates: Date[] | null = null;

    readonly today = new Date();

    constructor(
        /** Public so the template can read signals and option arrays directly. */
        readonly state: WorkspaceOverviewStateService,
        authService: AuthenticationService,
        private readonly router: Router,
        private readonly toast: ToastService,
    ) {
        this.state.init(authService.getUser() as IUser);
    }

    /** Export stub — real integration handled by the owning dev. */
    onExport(): void {
        this.toast.info('Export coming soon — backend integration pending.');
    }

    /**
     * Untapped Potential chip click handler. The widget emits the feature and
     * the container dispatches based on action type so the widget stays dumb.
     */
    onUntappedFeatureClick(feature: UntappedFeature): void {
        switch (feature.action) {
            case UntappedActionType.Navigate:
                if (feature.target) this.router.navigateByUrl(feature.target);
                break;
            case UntappedActionType.OpenDoc:
                // No target set for these chips — informational only, click does nothing
                break;
        }
    }
}
